import { ANALYTICS_NAVIGATION_MAP, DashboardTiles } from '@common/constants';
import { TilesProps } from '@common/types';
import { MenuItem } from '@cloudclevr/components';
import {
  useAddDashboardTile,
  useDashboardUpdateTileParams,
  useHandleTileBtnNavigate,
  useRemoveDashboardTile,
} from '@hooks/dashboard';
import { useLicencesAssignmentQuery } from '@hooks/licenses';
import { DoughnutChartTile } from '@pages/InsightsAndAnalytics/tiles/DoughnutChartTile';
import { useState } from 'react';
import { useDualUtilisationData, pickSummary } from '@hooks/summary';
import { toLicensesAssignment } from '@hooks/summary/adapters';

type Params = {
  vendorList: string[];
};

export const LicenseAssignment: React.FC<TilesProps> = ({
  params,
  id,
  classNames,
}) => {
  const [parameters, setParameters] = useState<Params>(JSON.parse(params));
  const useDashboardUpdateTileParamsMutation = useDashboardUpdateTileParams();
  const { handleButtonClick } = useHandleTileBtnNavigate(id);
  const { removeMenuItem } = useRemoveDashboardTile(id);
  const { addMenuItem } = useAddDashboardTile(
    DashboardTiles.LicenseAssignment,
    parameters
  );
  const isOnDashboardPage = !!id;

  const {
    data: licensesAssignmentData,
    isLoading,
    refetch,
  } = useDualUtilisationData({
    legacyParams: parameters,
    useLegacyQuery: useLicencesAssignmentQuery,
    mapSummary: (grouped) =>
      toLicensesAssignment(pickSummary(grouped, 'Subscriptions')),
  });

  const handleDropdown = (value: string[]) => {
    setParameters({
      vendorList: value,
    });

    if (isOnDashboardPage) {
      useDashboardUpdateTileParamsMutation.mutate({
        params: JSON.stringify({
          vendorList: value,
        }),
        tileId: id,
      });

      refetch();
    }
  };

  const sortMenuItems: MenuItem[] = [
    {
      id: '1',
      label: 'All Vendors',
      value: 'All',
      action: () => handleDropdown(['Microsoft', 'RingCentral']),
    },
    {
      id: '2',
      label: 'Microsoft',
      value: 'Microsoft',
      action: () => handleDropdown(['Microsoft']),
    },
    {
      id: '3',
      label: 'RingCentral',
      value: 'RingCentral',
      action: () => handleDropdown(['RingCentral']),
    },
  ];

  return (
    parameters && (
      <DoughnutChartTile
        dropdownMenuItemClasses={'!w-[300px]'}
        initialSelectedItem={sortMenuItems.find(
          (item) =>
            item.value ===
            (parameters.vendorList.length > 1
              ? 'All'
              : parameters.vendorList[0])
        )}
        contextData={isOnDashboardPage ? removeMenuItem : addMenuItem}
        classNames={'col-span-6 md:col-span-3 xl:col-span-2'}
        sortData={sortMenuItems}
        headerTitle={'Licence assignment'}
        isLoading={isLoading}
        data={
          licensesAssignmentData ? Object.values(licensesAssignmentData) : []
        }
        dataLabels={['Unassigned licences', 'Assigned licences']}
        buttonLabel={'Unassigned licence data'}
        linkPath={ANALYTICS_NAVIGATION_MAP['assignment']}
        handleButtonClick={handleButtonClick()}
      />
    )
  );
};
